// Suspended-school handling (backend: tenant_status.py).
//
// When Desk Ops suspends a school, every API call for it answers
// 403 {detail: {code: "tenant_suspended", billing_only, message, reason}}.
// The app makes those calls through many separate helpers (plain fetch in the
// teacher/student apps, axios in the shared context), so instead of teaching
// each one, a single watcher sees every response and announces the event:
//   - TenantSuspensionGuard signs teachers/students out to their school's
//     login page, which shows the suspension notice.
//   - InstitutionDashboard switches the owner/admin to billing-only mode.

export const TENANT_SUSPENDED_EVENT = 'deskey:tenant-suspended';

/** @param {unknown} detail  API error `detail` */
export const isTenantSuspended = (detail) => !!detail && typeof detail === 'object' && detail.code === 'tenant_suspended';

const announce = (detail) => {
  window.dispatchEvent(new CustomEvent(TENANT_SUSPENDED_EVENT, { detail }));
};

/**
 * Install once at startup, before the app renders.
 * @param {import('axios').AxiosStatic} axios  the shared axios instance
 */
export function installTenantSuspensionWatcher(axios) {
  if (window.__deskeyTenantSuspensionWatcher) return;
  window.__deskeyTenantSuspensionWatcher = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await nativeFetch(...args);
    if (res.status === 403) {
      // Read a copy so the caller can still consume the original body.
      res.clone().json()
        .then((body) => { if (isTenantSuspended(body?.detail)) announce(body.detail); })
        .catch(() => { /* non-JSON 403 — not ours */ });
    }
    return res;
  };

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && isTenantSuspended(detail)) announce(detail);
      return Promise.reject(error);
    },
  );
}
